import {
  Avatar,
  CollapseCard,
  FieldLabel,
  FlexBox,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import type {
  CustomCardIntegration,
  IntegrationTestResult,
} from 'lib-shared-types-api';
import { MutableRefObject, useState } from 'react';

import { FieldTemplate } from 'components/fieldTemplate';
import type { MagentoCardState } from 'components/settings/reducers/sections/magentoCards';
import type { SalesCardState } from 'components/settings/reducers/sections/salesforceCards';
import type { ShopifyCardState } from 'components/settings/reducers/sections/shopifyCards';
import { useBreakpointValue } from 'utils/mediaQuery';

import { editorMaxLines } from '../../../configuration';

import styles from './cardsTestResult.module.scss';
import { TestContent } from './components/testContent/testContent';

type OtherCards = MagentoCardState | SalesCardState | ShopifyCardState;

export type CardsTestResultProps = {
  card?: CustomCardIntegration | OtherCards;
  isLoading?: boolean;
  hasFailed?: boolean;
  testResult?: IntegrationTestResult;
  scrollRef?: MutableRefObject<HTMLDivElement | null>;
};

export const CardsTestResult = ({
  card,
  isLoading,
  testResult,
  hasFailed,
  scrollRef,
}: CardsTestResultProps): JSX.Element | null => {
  const isMediumScreen = useBreakpointValue({ base: true, lg: false });
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  if (isLoading) {
    return <Spinner data-testid="loadingTestResultId" />;
  }

  if (!card || !testResult) {
    return null;
  }

  const isCustomCard = 'request' in card;

  const title = isCustomCard
    ? (card as CustomCardIntegration).title
    : (card as OtherCards).hook.title;

  const createdAt = isCustomCard
    ? (card as CustomCardIntegration).created_at
    : (card as OtherCards).hook.created_at;

  const cardVersion = isCustomCard
    ? (card as CustomCardIntegration).card_version
    : (card as OtherCards).hook.card_version;

  const resultContent = testResult?.result?.contents;
  const rawJSON = testResult?.raw_json;

  return (
    <div ref={scrollRef} data-testid="testResultId">
      <Text.Heading className={styles.testResult}>Test result</Text.Heading>

      <FlexBox
        gap={isMediumScreen ? '250' : '400'}
        flexDirection={isMediumScreen ? 'column' : 'row'}
        margin="0 0 400 0"
      >
        <FieldTemplate
          readOnly
          label="JSON response"
          editorOptions={{ minLines: 1, maxLines: editorMaxLines }}
          input={{
            value: JSON.stringify(
              rawJSON ? JSON.parse(rawJSON) : testResult,
              null,
              2,
            ),
          }}
          meta={{
            warning: !resultContent
              ? 'The render response is empty'
              : undefined,
            error: hasFailed
              ? 'Call to retrieve the rendered test failed'
              : undefined,
          }}
        />
        {!isMediumScreen && <FlexBox className={styles.testResultDivider} />}

        <FlexBox flexDirection="column" gap="200">
          <FieldLabel>Rendered response</FieldLabel>

          <CollapseCard
            onToggle={() => setIsCollapsed(!isCollapsed)}
            isOpen={isCollapsed}
            className={styles.testResultCollapse}
          >
            <CollapseCard.Header
              avatar={<Avatar icon="circle-filled" />}
              className={styles.testResultCollapseHeader}
            >
              {title}
            </CollapseCard.Header>

            <CollapseCard.Content>
              <CollapseCard.Line label="">
                {resultContent && (
                  <TestContent
                    content={resultContent}
                    createdAt={createdAt}
                    cardVersion={cardVersion}
                  />
                )}
              </CollapseCard.Line>
            </CollapseCard.Content>
          </CollapseCard>
        </FlexBox>
      </FlexBox>
    </div>
  );
};
